import React from 'react';
import { PlusOutlined, MinusOutlined, DownloadOutlined } from '@ant-design/icons';
import { useInvalidate } from '@refinedev/core';
import { message } from 'antd';
import { SmartDataTable } from '@src/components/SmartDataTable';
import { useInventoryColumns } from './hooks/useInventoryColumns';
import { useInventoryFormFields } from './hooks/useInventoryFormFields';
import { StockAdjustmentForm } from './components/StockAdjustmentForm';
import { apiClient } from '@src/utils/axios';

export const InventoryPage = () => {
  const columns = useInventoryColumns();
  const getFormFields = useInventoryFormFields();
  const invalidate = useInvalidate();
  const [adjustmentModal, setAdjustmentModal] = React.useState<{
    visible: boolean;
    record: any;
    type: 'increase' | 'decrease';
  }>({
    visible: false,
    record: null,
    type: 'increase',
  });

  const handleStockAdjustment = (record: any, type: 'increase' | 'decrease') => {
    setAdjustmentModal({
      visible: true,
      record,
      type,
    });
  };

  const handleModalClose = () => {
    setAdjustmentModal({
      visible: false,
      record: null,
      type: 'increase',
    });
  };

  const handleAdjustmentSuccess = () => {
    // Invalidate the inventory list to refresh the table data
    invalidate({
      resource: 'inventory',
      invalidates: ['list', 'many'],
    });
    handleModalClose();
  };

  const handleDownloadReport = async (record: any) => {
    try {
      const response = await apiClient.get(`/inventory/export/${record.id}`, {
        responseType: 'blob', // Important for file downloads
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `inventory-report-${record.medicine_name || record.id}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.destroy();
      message.success('Report downloaded successfully!');
    } catch (error) {
      message.destroy();
      message.error('Failed to download report');
      console.error('Download error:', error);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SmartDataTable
        api={{
          resource: 'inventory',
        }}
        title="Medicine Inventory"
        columns={columns}
        search={{
          enabled: true,
          fields: [
            'medicine_name',
            'manufacturer',
            'supplier_name',
            'supplier_contact',
            'manufacturer_contact_person',
            'manufacturer_phone',
          ],
          placeholder: 'Search by medicine, manufacturer, or supplier...',
          debounceMs: 500,
        }}
        dynamicEditForm={{
          getForm: (record: any) => ({
            fields: getFormFields(record),
            title: 'Edit Inventory',
            submitText: 'Save Changes',
          }),
          getResource: () => 'inventory',
        }}
        formDisplayMode="modal"
        showCreate={false}
        showEdit={true}
        showDelete={false}
        showView={false}
        pageSize={10}
        pageOptions={[10, 20, 50, 100]}
        showSizeChanger={true}
        showQuickJumper={false}
        // showExport={true}
        showColumnVisibility={true}
        customActions={[
          {
            key: 'download-report',
            icon: <DownloadOutlined />,
            label: '',
            onClick: (record: any) => handleDownloadReport(record),
            type: 'default',
            tooltip: 'Download Report',
          },
          {
            key: 'increase-stock',
            icon: <PlusOutlined />,
            label: '',
            onClick: (record: any) => handleStockAdjustment(record, 'increase'),
            type: 'primary',
            tooltip: 'Increase Stock',
          },
          {
            key: 'decrease-stock',
            icon: <MinusOutlined />,
            label: '',
            onClick: (record: any) => handleStockAdjustment(record, 'decrease'),
            type: 'default',
            danger: true,
            tooltip: 'Decrease Stock',
          },
        ]}
        tableProps={{
          scroll: {
            x: 'max-content',
            y: 'calc(100vh - 300px)',
          },
        }}
        rowKey="id"
      />

      <StockAdjustmentForm
        visible={adjustmentModal.visible}
        onCancel={handleModalClose}
        record={adjustmentModal.record}
        adjustmentType={adjustmentModal.type}
        onSuccess={handleAdjustmentSuccess}
      />
    </div>
  );
};
